import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { useDemoMode } from "@/hooks/useSettings";
import {
  getConnectionStatus,
  onConnectionStatus,
  type ConnectionStatus,
} from "@/lib/chatClient";

type Size = "sm" | "md" | "lg";

function useViewport(): Size {
  const [size, setSize] = useState<Size>(() => {
    if (typeof window === "undefined") return "lg";
    const w = window.innerWidth;
    if (w < 640) return "sm";
    if (w < 1024) return "md";
    return "lg";
  });
  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setSize(w < 640 ? "sm" : w < 1024 ? "md" : "lg");
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return size;
}

export function DemoBadge({ className }: { className?: string }) {
  const { demoMode } = useDemoMode();
  const [status, setStatus] = useState<ConnectionStatus>(() => getConnectionStatus());
  const size = useViewport();

  useEffect(() => onConnectionStatus(setStatus), []);

  const isFallback = !demoMode && status === "degraded";
  const isConnected = !demoMode && !isFallback;

  let label: string;
  if (demoMode) {
    label = "MODALITÀ DEMO";
  } else if (isFallback) {
    label = "DEMO / FALLBACK";
  } else {
    // sm: smartphone → compatto
    // md: tablet → variante intermedia
    // lg: desktop → completo
    if (size === "sm") label = "AI CONNESSA";
    else if (size === "md") label = "AI DATACOM CONNESSA";
    else label = "ASSISTENTE AI DATACOM CONNESSO";
  }

  const tone = isConnected
    ? "border-success/30 bg-success/10 text-success"
    : "border-warning/30 bg-warning/10 text-warning";
  const dot = isConnected ? "bg-success" : "bg-warning";

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider",
        tone,
        className,
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full animate-pulse", dot)} />
      {label}
    </span>
  );
}